import {
  GridItem,
  LayoutGrid,
  RadialGradient,
  TypographyV3,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import {
  useAnimationControls,
  useInView,
  useReducedMotion,
  useScroll,
  useTransform,
} from 'framer-motion';
import { useTranslations } from 'next-intl';
import { forwardRef, type Ref, useEffect, useRef } from 'react';

import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';

import HeroCopy from './HomeHeroCopy';
import HomeHeroShopChiclet from './HomeHeroShopChiclet';
import tValue from './HomeHeroTValue';

interface HomeBottomHeroProps {
  readonly isTopHeroInView: boolean;
  readonly topHeroHeight: number;
}

const HomeBottomHero = forwardRef(function HomeBottomHero(
  { isTopHeroInView, topHeroHeight }: HomeBottomHeroProps,
  ref: Ref<HTMLDivElement>,
): JSX.Element {
  const t = useTranslations();
  const controls = useAnimationControls();
  const mounted = useRef(false);
  const motionRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();
  const isInView = useInView(motionRef);
  const target = useRef(null);
  const { scrollYProgress } = useScroll({
    target,
    offset: ['start start', 'end start'],
    layoutEffect: false,
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], ['0%', '15%']);

  useEffect(() => {
    const isMounted = mounted.current;
    const start = async () => {
      await controls.start({ scale: 1 });
    };
    const end = async () => {
      await controls.start({ scale: 1.05 });
    };
    if (isInView && isMounted) {
      if (!isTopHeroInView) {
        start().catch(() => {
          // do nothing
        });
      } else {
        end().catch(() => {
          // do nothing
        });
      }
    }

    return () => {
      controls.stop();
    };
  }, [isTopHeroInView, controls, isInView]);

  useEffect(() => {
    const isMounted = mounted.current;
    if (!isMounted && isInView) {
      mounted.current = true;
    }
  }, [isInView]);

  const handleHeroButtonFocus = (
    event: React.FocusEvent<HTMLAnchorElement | HTMLButtonElement>,
    topHero: boolean | undefined,
  ) => {
    if (!topHero) {
      event.preventDefault();
      event.stopPropagation();
      window.scrollTo({
        top: topHeroHeight,
        behavior: 'smooth',
      });
    }
  };

  return (
    <Motion
      el="section"
      data-cy="home-bottom-hero"
      ref={motionRef}
      className={cx(
        'sticky bottom-0 h-lvh bg-sandstone-200 lg:min-h-[50vw] lg:px-5 lg:pb-4',
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, type: 'tween', delay: 1 }}
    >
      <div ref={ref} className="h-full">
        <div ref={target} className="h-full">
          <LayoutGrid className="h-full content-between">
            <GridItem
              className="h-full overflow-hidden"
              colEnd={{ sm: 'full' }}
              colStart={{ sm: 'full' }}
              rowEnd={{ sm: 3 }}
              rowStart={{ sm: 1 }}
            >
              <div className="relative h-full md:hidden">
                <Motion
                  style={{
                    y: shouldReduceMotion ? 0 : parallaxY,
                    height: '100%',
                    position: 'relative',
                  }}
                >
                  <Image
                    alt={t('hero_bottom_img_alt')}
                    className="object-cover"
                    src="blue-sky/home/bottom-hero-mobile-3"
                    fill
                    sizes="(max-width: 768px) 100vw"
                  />
                </Motion>
                <RadialGradient
                  hex="#D7D6D4"
                  id="home_hero_mobile_gradient"
                  opacity={1}
                  position={[50, 25]}
                  className="pointer-events-none"
                  shape="circle"
                  size={40}
                />
              </div>
              <div
                className={cx(
                  'relative hidden h-full',
                  'md:block',
                  'lg:overflow-hidden lg:rounded-b-xl',
                )}
              >
                <Motion
                  animate={controls}
                  initial={{ scale: shouldReduceMotion ? 1 : 1.05 }}
                  transition={{ duration: 1.5, type: 'tween' }}
                  style={{
                    y: shouldReduceMotion ? 0 : parallaxY,
                    height: '100%',
                    position: 'relative',
                  }}
                >
                  <Image
                    alt={t('hero_bottom_img_alt')}
                    className="object-cover"
                    src="blue-sky/home/bottom-hero-desktop-2"
                    fill
                    sizes="100vw"
                  />
                </Motion>
              </div>
            </GridItem>
            <GridItem
              className="z-10 clip-polygon"
              colEnd={{ sm: 'main' }}
              colStart={{ sm: 'main' }}
              rowEnd={{ sm: 2 }}
              rowStart={{ sm: 1 }}
            >
              <Motion
                initial={{ opacity: 0, translateY: 25, filter: 'blur(5px)' }}
                animate={{ opacity: 1, translateY: 0, filter: 'blur(0)' }}
                transition={{ duration: 1.25, type: 'tween', delay: 1 }}
              >
                <div role="presentation">
                  <TypographyV3
                    Element="h2"
                    variant="h1"
                    className={cx(
                      'flex flex-col items-center pt-[22vh]',
                      'md:items-start md:pt-40',
                      'xl:w-11/12 xl:pt-[22vh]',
                    )}
                    color="dark"
                  >
                    {t.rich('revolutionary_smart_ring_title', tValue)}
                  </TypographyV3>
                </div>
              </Motion>
            </GridItem>
            <GridItem
              className="z-10 hidden self-end md:block"
              colEnd={{ md: 10, lg: 10, xl: 8 }}
              colStart={{ md: 'main', lg: 'main', xl: 'main' }}
              rowEnd={{ sm: 3 }}
              rowStart={{ sm: 2 }}
            >
              <HeroCopy
                color="dark"
                onHeroButtonFocus={handleHeroButtonFocus}
              />
            </GridItem>
            <GridItem
              className="z-10 flex flex-col items-center self-end"
              colEnd={{ sm: 'main', md: 'main', lg: 'main', xl: 'main' }}
              colStart={{ sm: 'main', md: 15, lg: 15, xl: 18 }}
              rowEnd={{ sm: 3 }}
              rowStart={{ sm: 2 }}
            >
              <div className="md:hidden">
                <HeroCopy
                  color="dark"
                  onHeroButtonFocus={handleHeroButtonFocus}
                />
              </div>
              <HomeHeroShopChiclet onHeroButtonFocus={handleHeroButtonFocus} />
            </GridItem>
          </LayoutGrid>
        </div>
      </div>
    </Motion>
  );
});

export default HomeBottomHero;
